/** biome-ignore-all lint/suspicious/noConsole: Output */

import { readFile } from "node:fs/promises";
import { Readable } from "node:stream";
import { ConsoleLogEntry, LogFormatter, LogLevel } from "@zuplo/core/cli";
import chalk, { ChalkInstance } from "chalk";

const logFormatter = new LogFormatter(async (path: string) => {
  return readFile(path, { encoding: "utf8" });
});

// biome-ignore lint/suspicious/noControlCharactersInRegex: matches ANSI escapes
const ANSI_ESCAPE = /\u001b\[[0-9;]*m/g;

/** `2026-10-11T01:42:43.470549Z  INFO zuplo_gateway: listening addr=...` */
const GATEWAY_TRACING_LINE =
  /^\d{4}-\d{2}-\d{2}T[\d:.]+Z\s+(TRACE|DEBUG|INFO|WARN|ERROR)\s+[\w:]+:\s?(.*)$/;

/** Diagnostics and lifecycle lines `zuplo-gateway dev --watch` prints. */
const GATEWAY_PLAIN_LINES: [RegExp, LogLevel][] = [
  [/^error: /, "error"],
  [/^warning: /, "warn"],
  [/^(note|hint): /, "info"],
  [/^rebuild failed; still serving the previous build$/, "warn"],
  [/^watching .+ for changes$/, "info"],
  [/^.+ changed; rebuilding$/, "info"],
  [/^rebuilt in \d+ ms$/, "info"],
];

/**
 * The level and message of a line the Rust gateway (`zuplo-gateway`, the
 * opt-in managed-dedicated runtime) writes, or undefined for any other line.
 * The gateway logs through `tracing` (with its own timestamp, which is
 * dropped: the formatted line gets ours) and prints build diagnostics as
 * `error:` / `warning:` / `note:` / `hint:` lines. Without this every one of
 * them would be shown as an error.
 */
export function classifyGatewayLine(
  line: string
): { level: LogLevel; message: string } | undefined {
  const plain = line.replace(ANSI_ESCAPE, "");
  const tracing = GATEWAY_TRACING_LINE.exec(plain);
  if (tracing) {
    const levels: Record<string, LogLevel> = {
      // biome-ignore-start lint/style/useNamingConvention: tracing level names
      TRACE: "debug",
      DEBUG: "debug",
      INFO: "info",
      WARN: "warn",
      ERROR: "error",
      // biome-ignore-end lint/style/useNamingConvention: tracing level names
    };
    return { level: levels[tracing[1]], message: tracing[2] };
  }
  for (const [pattern, level] of GATEWAY_PLAIN_LINES) {
    if (pattern.test(plain)) {
      return { level, message: plain };
    }
  }
  return undefined;
}

export function createRuntimeStdioHandler(options?: {
  prefix?: string;
  prefixColor?: ChalkInstance;
}) {
  return function handleRuntimeStdio(stdout: Readable, stderr: Readable) {
    const { prefix, prefixColor } = options ?? {};
    let stdoutAccumulator = "";
    let stderrAccumulator = "";

    const classifiers = {
      // Is this chunk a big chonky barf from workerd that we want to hijack to cleanup/ignore?
      isBarf(chunk: string) {
        const containsLlvmSymbolizerWarning = chunk.includes(
          "Not symbolizing stack traces because $LLVM_SYMBOLIZER is not set"
        );
        const containsRecursiveIsolateLockWarning = chunk.includes(
          "took recursive isolate lock"
        );
        // Matches stack traces from workerd
        //  - on unix: groups of 9 hex digits separated by spaces
        //  - on windows: groups of 12 hex digits, or a single digit 0, separated by spaces
        const containsHexStack = /stack:( (0|[a-f\d]{4,})){3,}/.test(chunk);

        return (
          containsLlvmSymbolizerWarning ||
          containsRecursiveIsolateLockWarning ||
          containsHexStack
        );
      },
      // Is this chunk an Address In Use error?
      isAddressInUse(chunk: string) {
        return chunk.includes("Address already in use; toString() = ");
      },
      isWarning(chunk: string) {
        return /\.c\+\+:\d+: warning:/.test(chunk);
      },
      isCodeMovedWarning(chunk: string) {
        return /CODE_MOVED for unknown code block/.test(chunk);
      },
      isAccessViolation(chunk: string) {
        return chunk.includes("access violation;");
      },
      isZenoLog(chunk: string) {
        return chunk.startsWith("[ZENO]:");
      },
    };

    stdout.on("data", (chunk: Buffer | string) => {
      const fullStreamOutput = `${stdoutAccumulator}${chunk}`;

      let currentLogsStr = "";
      const lastNewlineIdx = fullStreamOutput.lastIndexOf("\n");

      if (lastNewlineIdx > 0) {
        // Extract complete logs up to the last newline
        currentLogsStr = fullStreamOutput.slice(0, lastNewlineIdx);
        // Save the partial/incomplete log for next iteration
        stdoutAccumulator = fullStreamOutput.slice(lastNewlineIdx + 1);
      } else {
        // No complete log found, accumulate entire output
        stdoutAccumulator = fullStreamOutput;
        return;
      }

      const lines = currentLogsStr.split("\n");

      for (let line of lines) {
        line = line.trim();
        if (!line) continue;
        const gateway = classifyGatewayLine(line);

        if (classifiers.isBarf(line)) {
          // this is a big chonky barf from workerd that we want to hijack to cleanup/ignore

          // CLEANABLE:
          // there are no known cases to cleanup yet
          // but, as they are identified, we will do that here

          // IGNORABLE:
          // anything else not handled above is considered ignorable
          // so send it to the debug logs which are discarded unless
          // the user explicitly sets a logLevel indicating they care
          logConsole("debug", line, prefix, prefixColor);
        }

        // known case: warnings are not info, log them as such
        else if (classifiers.isWarning(line)) {
          logConsole("warn", line, prefix, prefixColor);
        } else if (classifiers.isZenoLog(line)) {
          // Route to the specified logging level
          const levels = ["debug", "info", "warn", "error"];
          const level = levels.find((level) =>
            line.includes(`[${level.toUpperCase()}]`)
          );
          // Strip the prefixes for zeno and the zeno logging level
          line = line.substring("[ZENO]:".length);
          line = line.substring(`[${level?.toUpperCase()}]`.length);
          if (level) {
            logConsole(level as LogLevel, line, prefix, prefixColor);
          } else {
            logConsole("info", line, prefix, prefixColor);
          }
        }

        // the Rust gateway's own log and diagnostic lines
        else if (gateway) {
          logConsole(gateway.level, gateway.message, prefix, prefixColor);
        }

        // anything not explicitly handled above should be logged as info (via stdout)
        else {
          logConsole("error", line, prefix, prefixColor);
        }
      }
    });

    stderr.on("data", (chunk: Buffer | string) => {
      const fullStreamOutput = `${stderrAccumulator}${chunk}`;

      let currentLogsStr = "";
      const lastNewlineIdx = fullStreamOutput.lastIndexOf("\n");

      if (lastNewlineIdx > 0) {
        // Extract complete logs up to the last newline
        currentLogsStr = fullStreamOutput.slice(0, lastNewlineIdx);
        // Save the partial/incomplete log for next iteration
        stderrAccumulator = fullStreamOutput.slice(lastNewlineIdx + 1);
      } else {
        // No complete log found, accumulate entire output
        stderrAccumulator = fullStreamOutput;
        return;
      }

      const lines = currentLogsStr.split("\n");

      for (let line of lines) {
        line = line.trim();
        if (!line) continue;
        const gateway = classifyGatewayLine(line);

        if (classifiers.isBarf(line)) {
          // this is a big chonky barf from workerd that we want to hijack to cleanup/ignore

          // CLEANABLE:
          // known case to cleanup: Address in use errors
          if (classifiers.isAddressInUse(line)) {
            const address = line.match(
              /Address already in use; toString\(\) = (.+)\n/
            )?.[1];

            logConsole(
              "error",
              `Address already in use (${address}). Please check that you are not already running a server on this address or specify a different port with --port.`,
              prefix,
              prefixColor
            );

            // Log the original error to the debug logs.
            logConsole("debug", line, prefix, prefixColor);
          }
          // In the past we have seen Access Violation errors on Windows, which may be caused by an outdated
          // version of the Windows OS or the Microsoft Visual C++ Redistributable.
          // See https://github.com/cloudflare/workers-sdk/issues/6170#issuecomment-2245209918
          else if (classifiers.isAccessViolation(line)) {
            let error = "There was an access violation in the runtime.";
            if (process.platform === "win32") {
              error +=
                "\nOn Windows, this may be caused by an outdated Microsoft Visual C++ Redistributable library.\n" +
                "Check that you have the latest version installed.\n" +
                "See https://learn.microsoft.com/en-us/cpp/windows/latest-supported-vc-redist.";
            }
            logConsole("error", error, prefix, prefixColor);

            // Log the original error to the debug logs.
            logConsole("debug", line, prefix, prefixColor);
          }

          // IGNORABLE:
          // anything else not handled above is considered ignorable
          // so send it to the debug logs which are discarded unless
          // the user explicitly sets a logLevel indicating they care
          else {
            logConsole("debug", line, prefix, prefixColor);
          }
        }

        // known case: warnings are not errors, log them as such
        else if (classifiers.isWarning(line)) {
          logConsole("warn", line, prefix, prefixColor);
        }

        // known case: "error: CODE_MOVED for unknown code block?", warning for workerd devs, not application devs
        else if (classifiers.isCodeMovedWarning(line)) {
          // ignore entirely, don't even send it to the debug log file
        }

        // Handle Zeno error logs
        else if (classifiers.isZenoLog(line)) {
          // Strip the prefixes
          line = line.substring("[ZENO]:".length);
          line = line.substring("[ERROR]".length);
          logConsole("error", line, prefix, prefixColor);
        }

        // the Rust gateway's own log and diagnostic lines
        else if (gateway) {
          logConsole(gateway.level, gateway.message, prefix, prefixColor);
        }

        // anything not explicitly handled above should be logged as an error (via stderr)
        else {
          logConsole("error", line, prefix, prefixColor);
        }
      }
    });
  };
}

function logConsole(
  level: LogLevel,
  data: string,
  prefix?: string,
  prefixColor?: ChalkInstance
) {
  void logConsoleAsync(level, data, prefix, prefixColor);
}

/**
 * This is what is logged when the runtime calls console.log, etc.
 */
async function logConsoleAsync(
  level: LogLevel,
  data: string,
  prefix?: string,
  prefixColor?: ChalkInstance
) {
  let outputPrefix = "";
  if (prefix && prefixColor) {
    const paddedName = prefix.padEnd(10);
    outputPrefix = prefixColor(paddedName) + chalk.gray(" | ");
  }

  try {
    if (!data) {
      return;
    }

    // This shouldn't happen, but just in case
    if (typeof data !== "string") {
      console[level](outputPrefix + data);
      return;
    }

    let message = data?.trim();
    if (!message || message?.length === 0) {
      return;
    }

    // Probably a JSON object
    if (message.startsWith(`{`) && message.endsWith(`}`)) {
      let logEntry: ConsoleLogEntry;
      try {
        logEntry = JSON.parse(message);
      } catch {
        console[level](outputPrefix + data);
        return;
      }
      await Promise.all(
        logEntry.messages.map(async (msg) => {
          const line = await logFormatter.formatStructured(logEntry, msg);
          console[level](outputPrefix + line);
        })
      );
    } else {
      message = await logFormatter.formatMessage(message);

      const line = logFormatter.formatLine(level, message);
      console[level](outputPrefix + line);
    }
  } catch {
    const line = logFormatter.formatLine(level, data);

    console[level](outputPrefix + line);
  }
}
